import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import { GoogleMap, LoadScript, DirectionsRenderer } from '@react-google-maps/api';
import { Autocomplete, TextField } from '@mui/material';
import {
  ColumnDirective,
  ColumnsDirective,
  Filter,
  GridComponent,
  Group,
  Inject,
  Page,
  Sort,
} from '@syncfusion/ej2-react-grids';
import customAxios from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';

const containerStyle = {
  width: '100%',
  height: '70vh',
};

// Default center (somewhere neutral)
const defaultCenter = {
  lat: 53.638755,
  lng: 7.410353,
};

const MapRouteView = ({ open, setOpen, mapCordinateDetails, routeDetails, getRouteDetails, siteMainData }) => {
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [center, setCenter] = useState(defaultCenter);
  const [zoom, setZoom] = useState(15); // Zoomed out by default
  const [directionsResponse, setDirectionsResponse] = useState(null);

  //all cordinates data
  const [entranceCoordinates, setEntranceCoordinates] = useState(null);
  const [destinationCoordinates, setDestinationCoordinates] = useState(null);
  const directionsRendererRef = useRef(null);
  const [currentPolyline, setCurrentPolyline] = useState(null);

  const [currentRoutes, setCurrentRoutes] = useState(null);

  const [activeRouteForSaving, setActiveRouteForSaving] = useState(null);

  const [gridKey, setGridKey] = useState(0); // Force grid re-render

  // 🆕 Track if route is being dragged by user
  const isUserDraggingRef = useRef(false);
  const routeLoadedRef = useRef(false);

  // 🆕 Local state to manage distances in real-time
  const [localRouteDetails, setLocalRouteDetails] = useState([]);

  // 🆕 Calculate initial distances for all routes when entrance is selected
  useEffect(() => {
    const calculateInitialDistances = async () => {
      if (!entranceCoordinates || !routeDetails || routeDetails.length === 0) {
        setLocalRouteDetails(routeDetails || []);
        return;
      }

      console.log('Calculating initial distances for all routes...');

      const updatedRoutes = routeDetails.map(route => {
        // If route already has a saved distance from DB, keep it
        if (route.distance && route.distance !== 'N/A' && route.polyLine) {
          return route;
        }

        // Calculate straight-line distance for routes without saved distance
        const destLat = route.destinationLatitude || route.latitude;
        const destLng = route.destinationLongitude || route.longitude;

        if (destLat && destLng && entranceCoordinates.latitude && entranceCoordinates.longitude) {
          const distance = haversineDistance(
            entranceCoordinates.latitude,
            entranceCoordinates.longitude,
            destLat,
            destLng,
          ).toFixed(2);

          return {
            ...route,
            distance: `${distance} km`,
          };
        }

        return route;
      });

      setLocalRouteDetails(updatedRoutes);
    };

    calculateInitialDistances();
  }, [routeDetails, entranceCoordinates]);

  function haversineDistance(lat1, lon1, lat2, lon2) {
    const toRad = deg => (deg * Math.PI) / 180;

    const R = 6371; // Radius of Earth in km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // distance in km
  }

  // async function getRoutes() {
  //   if (siteMainData) {
  //     try {
  //       const res = await customAxios.get(`/CRM/GetCustomerLocations/?siteAssesmentID=${siteMainData}`, {
  //         headers: authHeader(),
  //       });

  //       if (res.status === 200) {
  //         const transformed = res.data.map(item => {
  //           let distance = null;

  //           if (entranceCoordinates?.latitude && entranceCoordinates?.longitude) {
  //             distance = haversineDistance(
  //               entranceCoordinates.latitude,
  //               entranceCoordinates.longitude,
  //               item.latitude,
  //               item.longitude,
  //             ).toFixed(2);
  //           }

  //           return {
  //             destination: item.destinationName,
  //             distance: distance ? `${distance} km` : 'N/A',
  //             ...item,
  //           };
  //         });

  //         setCurrentRoutes(transformed);
  //       }
  //     } catch (err) {
  //       console.error('Error fetching routes:', err);
  //     }
  //   }
  // }

  // UseEffect gets called on initial render to set entrance from mapCordinatesDetails
  useEffect(() => {
    if (open && mapCordinateDetails?.length > 0) {
      for (let i = 0; i < mapCordinateDetails.length; i++) {
        const item = mapCordinateDetails[i];
        if (item?.mapLabel?.toLowerCase().includes('entrance')) {
          setEntranceCoordinates(item);
          console.log('Auto-selected entrance:', item.mapLabel);
          break;
        }
      }
    }
  }, [open, mapCordinateDetails]);

  const geocodeLocation = (location = 'Pune') => {
    if (!window.google || !window.google.maps) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: location }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const loc = results[0].geometry.location;
        setCenter({ lat: loc.lat(), lng: loc.lng() });
      } else {
        alert('Geocode was not successful for the following reason: ' + status);
      }
    });
  };

  const handleClose = () => {
    setOpen(false);
    setDestinationCoordinates(null);
    setEntranceCoordinates(null);
    setActiveRouteForSaving(null);
    setDirectionsResponse(null);
    setGridKey(0);
    setLocalRouteDetails([]);
  };

  // 🆕 Function to update distance in grid when polyline is dragged
  const updateDistanceInGrid = () => {
    if (!directionsRendererRef.current || !activeRouteForSaving) return;

    const updatedDirections = directionsRendererRef.current.getDirections();
    const capturedRoutes = updatedDirections?.routes;

    if (capturedRoutes && capturedRoutes.length > 0) {
      const distance = capturedRoutes[0].legs;
      if (distance.length > 0) {
        const distanceText = distance[0]['distance']['text'];
        const currentDistance = parseFloat(distanceText);

        console.log('Updating distance in grid:', currentDistance);

        // Update local route details with new distance
        setLocalRouteDetails(prevRoutes =>
          prevRoutes.map(route =>
            route.destination === activeRouteForSaving.destination ? { ...route, distance: `${currentDistance} km` } : route,
          ),
        );
      }
    }
  };

  const handleSave = async () => {
    console.log('HandleSave called', {
      hasDirectionsRenderer: !!directionsRendererRef.current,
      hasActiveRoute: !!activeRouteForSaving,
      hasEntrance: !!entranceCoordinates,
    });

    if (!directionsRendererRef.current) {
      alert('No route renderer available');
      return;
    }

    if (!activeRouteForSaving) {
      alert('No active route selected');
      return;
    }

    if (!entranceCoordinates) {
      alert('No entrance selected');
      return;
    }

    const updatedDirections = directionsRendererRef.current.getDirections();
    const capturedRoutes = updatedDirections.routes;
    let currentDistance = 0;

    if (capturedRoutes.length > 0) {
      const distance = capturedRoutes[0].legs;
      if (distance.length > 0) {
        currentDistance = parseFloat(distance[0]['distance']['text']);
      }

      // Build updated route object
      const obj = {
        ...activeRouteForSaving,
        distance: currentDistance,
        polyLine: capturedRoutes[0]['overview_polyline'],
        entrance: entranceCoordinates.mapLabel,
        entranceLatitude: entranceCoordinates.latitude,
        entranceLongitude: entranceCoordinates.longitude,
      };

      try {
        let res;
        // 🚀 Check if route exists in DB (id present and from SiteDetailsMapRoutes)
        if (obj.id && typeof obj.id === 'number' && obj.id > 0) {
          // existing route -> update
          res = await customAxios.post('/SiteDetails/EditSiteDetailsMapRoutes', obj, {
            headers: authHeader(),
          });
        } else {
          // new route -> create
          const createObj = { ...obj };
          delete createObj.id; // ensure no invalid id gets sent
          res = await customAxios.post('/SiteDetails/AddSiteDetailsMapRoutes', createObj, {
            headers: authHeader(),
          });
        }

        if (res.status === 200) {
          await getRouteDetails(obj.siteAssessmentID);
          setGridKey(prev => prev + 1); // Force grid refresh
        }
      } catch (error) {
        console.error('Error saving route:', error);
        alert('Failed to save route. Please try again.');
      }

      console.log(capturedRoutes[0]['overview_polyline'], 'updated Direction');
    }
  };

  const handleEntranceCoordinatesChange = newVal => {
    console.log('Entrance changed to:', newVal);
    setEntranceCoordinates(newVal);

    // If there's an active route, recalculate it with the new entrance
    if (activeRouteForSaving && newVal) {
      console.log('Recalculating route with new entrance');

      // Clear current route first
      setDirectionsResponse(null);

      // Small delay to ensure state update, then recalculate
      setTimeout(() => {
        // Create route object with cleared polyLine to force fresh calculation
        const routeToRecalculate = {
          ...activeRouteForSaving,
          polyLine: null, // Force fresh calculation, not saved route
        };

        handleRouteButtonClick(routeToRecalculate);
      }, 100);
    }

    // Update map center to new entrance location
    if (newVal && mapRef.current) {
      mapRef.current.panTo({
        lat: newVal.latitude,
        lng: newVal.longitude,
      });
      mapRef.current.setZoom(14);
    }
  };

  const renderSavedRouteAsDirections = polylineString => {
    if (!window.google || !window.google.maps) return;

    try {
      const decodedPath = window.google.maps.geometry.encoding.decodePath(polylineString);

      if (decodedPath.length < 2) {
        console.warn('Polyline has too few points.');
        return;
      }

      const downsamplePath = (path, maxWaypoints = 15) => {
        // Max total points = origin + destination + waypoints = maxWaypoints + 2
        const maxTotalPoints = maxWaypoints + 2;

        if (path.length <= maxTotalPoints) return path;

        const interval = Math.floor(path.length / (maxTotalPoints - 1));
        const downsampled = [path[0]];

        for (let i = interval; i < path.length - 1 && downsampled.length < maxTotalPoints - 1; i += interval) {
          downsampled.push(path[i]);
        }

        downsampled.push(path[path.length - 1]);
        return downsampled;
      };

      const simplifiedPath = downsamplePath(decodedPath, 15);

      const origin = simplifiedPath[0];
      const destination = simplifiedPath[simplifiedPath.length - 1];

      const waypoints = simplifiedPath.slice(1, -1).map(location => ({
        location,
        stopover: false,
      }));

      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin,
          destination,
          waypoints,
          travelMode: 'DRIVING',
          optimizeWaypoints: false,
        },
        (result, status) => {
          if (status === 'OK') {
            setDirectionsResponse(result);
          } else {
            console.error(`Directions request failed: ${status}`);
          }
        },
      );
    } catch (error) {
      console.error('Error decoding or rendering route:', error);
    }
  };

  const rowDataBound = args => {
    const isPolylinePresent = args.data.polyLine;
    console.log(args.data, 'dataH');

    if (isPolylinePresent) {
      args.row.style.backgroundColor = '#d4edda'; // light green
    }
  };

  const handleRouteButtonClick = route => {
    if (!entranceCoordinates) {
      alert('Select Entrance');
      return;
    }
    if (
      !entranceCoordinates.latitude ||
      !entranceCoordinates.longitude ||
      typeof entranceCoordinates.latitude !== 'number' ||
      typeof entranceCoordinates.longitude !== 'number'
    ) {
      console.error('Invalid entrance coordinates:', entranceCoordinates);
      alert('Invalid entrance coordinates. Please select a valid entrance.');
      return;
    }

    // Check for destination coordinates
    const destLat = route.destinationLatitude || route.latitude;
    const destLng = route.destinationLongitude || route.longitude;

    // Validate destination coordinates
    if (!destLat || !destLng || typeof destLat !== 'number' || typeof destLng !== 'number') {
      console.error('Invalid destination coordinates:', route);
      alert('Invalid destination coordinates for this route.');
      return;
    }

    setCurrentPolyline(route?.polyLine);
    setDestinationCoordinates(route);
    setActiveRouteForSaving(route);

    // 🆕 Reset drag tracking when new route is selected
    isUserDraggingRef.current = false;
    routeLoadedRef.current = false;

    setGridKey(prev => prev + 1);
    if (route.polyLine) {
      renderSavedRouteAsDirections(route.polyLine);
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: {
          lat: Number(entranceCoordinates.latitude),
          lng: Number(entranceCoordinates.longitude),
        },
        destination: {
          lat: Number(destLat),
          lng: Number(destLng),
        },
        travelMode: 'DRIVING',
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirectionsResponse(result);
          // Mark that route has been loaded
          setTimeout(() => {
            routeLoadedRef.current = true;
          }, 500);
          // Force another re-render after directions load
          setGridKey(prev => prev + 1);
        } else {
          console.error(`Directions request failed due to ${status}`);
          alert(`Unable to calculate route: ${status}`);
          // setActiveRouteForSaving(null);
          setGridKey(prev => prev + 1);
        }
      },
    );
  };

  const handleResetButtonClick = async route => {
    let obj = { ...route };

    // if (route.polyLine === null) {
    //   return;
    // }

    obj['distance'] = null;
    obj['polyLine'] = null;

    try {
      const res = await customAxios.post('/SiteDetails/EditSiteDetailsMapRoutes', obj, {
        headers: authHeader(),
      });

      if (res.status == 200) {
        // Clear the current route display first
        setDirectionsResponse(null);
        // setActiveRouteForSaving(null);

        // Refresh the grid data
        getRouteDetails(obj.siteAssessmentID);

        // Small delay to ensure grid update completes, then recalculate route
        setTimeout(() => {
          // Create a clean route object without the polyLine
          const cleanRoute = { ...route, polyLine: null, distance: null };
          handleRouteButtonClick(cleanRoute);
        }, 100);

        setGridKey(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error resetting route:', error);
      alert('Failed to reset route. Please try again.');
    }
  };

  const getRouteTemplate = rowData => {
    const isSelected = rowData?.destination === activeRouteForSaving?.destination;

    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Button
          color={isSelected ? 'secondary' : 'primary'}
          variant={isSelected ? 'contained' : 'text'}
          size="small"
          onClick={() => handleRouteButtonClick(rowData)}>
          {rowData.destination}
        </Button>
      </div>
    );
  };

  const saveRouteTemplate = rowData => {
    const isActiveRoute = activeRouteForSaving;
    const showSaveButton = entranceCoordinates && isActiveRoute && directionsResponse;

    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {showSaveButton && (
          <Button variant="contained" color="primary" size="small" onClick={() => handleSave()}>
            Save
          </Button>
        )}
      </div>
    );
  };

  const getResetRouteTemplate = rowData => {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Button size="small" color="error" variant="contained" onClick={() => handleResetButtonClick(rowData)}>
          Reset
        </Button>
      </div>
    );
  };

  const filterSettings = {
    type: 'Excel',
  };

  const mapRef = useRef(null);

  const handleMapLoad = map => {
    mapRef.current = map;
    setIsMapLoaded(true);

    if (entranceCoordinates) {
      map.panTo({ lat: entranceCoordinates.latitude, lng: entranceCoordinates.longitude });
      map.setZoom(5);
    } else {
      map.panTo(center);
      map.setZoom(18);
    }
  };

  return (
    <>
      <Dialog maxWidth="xl" open={open} onClose={handleClose} disableEscapeKeyDown={true} disableAutoFocus>
        <DialogTitle style={{ paddingBlock: '.25rem' }}>
          <Typography style={{ fontSize: '1.5rem' }} component="div" variant="subtitle2">
            Geo Locations
          </Typography>
        </DialogTitle>
        <DialogContent style={{ width: '95vw', height: '80vh' }}>
          <Box
            noValidate
            component="div"
            className="SiteInfoDialog"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              m: 'auto',
              width: '100%',
              gap: '1rem',
            }}>
            <div style={{ display: 'flex', gap: '1rem', flexDirection: 'row-reverse' }}>
              <div style={{ position: 'relative', height: '100%', flex: 1 }}>
                <LoadScript
                  onLoad={() => setIsScriptLoaded(true)}
                  libraries={['geometry']}
                  googleMapsApiKey="AIzaSyA94J1dEXShLTlOZmIZbmOT31Qyo2g-2OU">
                  <GoogleMap
                    mapContainerStyle={containerStyle}
                    zoom={zoom}
                    mapTypeId="hybrid"
                    options={{
                      mapTypeId: 'hybrid',
                      streetViewControl: true,
                      mapTypeControl: true,
                      rotateControl: true,
                    }}
                    onLoad={handleMapLoad}>
                    {directionsResponse && (
                      <DirectionsRenderer
                        onDirectionsChanged={() => {
                          // 🆕 Only update distance if route has loaded AND user is dragging
                          if (routeLoadedRef.current && isUserDraggingRef.current) {
                            console.log('direction changed - user dragged route');
                            setTimeout(() => {
                              updateDistanceInGrid();
                              isUserDraggingRef.current = false; // Reset after update
                            }, 100);
                          }
                        }}
                        options={{
                          draggable: true,
                        }}
                        directions={directionsResponse}
                        onLoad={directionsRenderer => {
                          directionsRendererRef.current = directionsRenderer;

                          // 🆕 Add listeners to track when user starts dragging
                          if (window.google && window.google.maps) {
                            const addDragListeners = () => {
                              const poly = directionsRenderer.getDirections();
                              if (poly) {
                                // Listen for drag start on the route polyline
                                window.google.maps.event.addListener(directionsRenderer, 'directions_changed', () => {
                                  if (routeLoadedRef.current) {
                                    isUserDraggingRef.current = true;
                                  }
                                });
                              }
                            };

                            setTimeout(addDragListeners, 500);
                          }
                        }}
                      />
                    )}
                  </GoogleMap>
                </LoadScript>
              </div>
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  flex: 0.5,
                  display: 'flex',
                  justifyContent: 'start',
                  gap: '0.5rem',
                  flexDirection: 'column',
                  alignItems: 'start',
                  paddingTop: '0.35rem',
                }}>
                <Autocomplete
                  size="small"
                  disablePortal
                  name="Entrance"
                  value={entranceCoordinates}
                  options={mapCordinateDetails}
                  onChange={(event, newValue) => handleEntranceCoordinatesChange(newValue)}
                  sx={{ fontSize: '0.8rem' }}
                  isOptionEqualToValue={option =>
                    option.mapLabel === entranceCoordinates ? entranceCoordinates.mapLabel : null
                  }
                  getOptionLabel={option => option.mapLabel}
                  renderInput={params => <TextField label={'Entrance'} {...params} size="small" fullWidth />}
                  fullWidth
                />

                <div style={{ height: 'calc(100vh - 260px)', width: '100%' }}>
                  <GridComponent
                    dataSource={localRouteDetails}
                    filterSettings={filterSettings}
                    allowSorting={true}
                    allowTextWrap={true}
                    allowResizing={true}
                    allowMultiSorting={true}
                    allowReordering={true}
                    allowFiltering={true}
                    style={{
                      minHeight: 'auto !important',
                    }}
                    height="100%"
                    rowDataBound={rowDataBound}
                    key={`grid-${gridKey}`}>
                    <ColumnsDirective>
                      <ColumnDirective
                        field="destination"
                        headerText="Destination"
                        width="100"
                        template={getRouteTemplate}
                      />
                      <ColumnDirective field="distance" headerText="Distance" width="100" />
                      <ColumnDirective headerText="Save" width="100" template={saveRouteTemplate} />
                      <ColumnDirective headerText="Reset Route" width="100" template={getResetRouteTemplate} />
                    </ColumnsDirective>
                    <Inject services={[Page, Sort, Filter, Group]} />
                  </GridComponent>
                </div>
              </div>
            </div>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MapRouteView;
